import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { InvoiceTemplate } from "@/components/InvoiceTemplate";
import { Download, ArrowLeft, Printer, CreditCard, FileText } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ViewInvoicePage() {
  const [searchParams] = useSearchParams();
  const invoiceNumber = searchParams.get("invoice");
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const templateRef = useRef(null);

  useEffect(() => {
    if (invoiceNumber) {
      fetchInvoice();
    } else {
      toast.error("No invoice number provided");
      navigate("/dashboard");
    }
  }, [invoiceNumber]);

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceNumber}`);
      setInvoice(response.data);
    } catch (error) {
      console.error("Error fetching invoice:", error);
      toast.error("Invoice not found");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!templateRef.current) return;
    
    setDownloading(true);
    try {
      const canvas = await html2canvas(templateRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
      });
      
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;
      
      pdf.addImage(imgData, "PNG", imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      
      const docType = invoice?.document_type === "quote" ? "Quote" : "Invoice";
      pdf.save(`ProToCall_${docType}_${invoice?.invoice_number}.pdf`);
      
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-700";
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      case "overdue":
        return "bg-red-100 text-red-700";
      case "partial":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  const isQuote = invoice?.document_type === "quote";

  return (
    <div className="min-h-screen bg-slate-100 py-8" data-testid="view-invoice-page">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-slate-600"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-slate-900">
                  {isQuote ? "Quotation" : "Invoice"} #{invoice?.invoice_number}
                </h1>
                <Badge className={getStatusColor(invoice?.status)}>
                  {invoice?.status}
                </Badge>
              </div>
              <p className="text-sm text-slate-500">
                Created on {new Date(invoice?.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handlePrint}
              className="border-slate-300"
              data-testid="print-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {downloading ? "Generating..." : "Download PDF"}
            </Button>
            {invoice?.status !== "paid" && !isQuote && (
              <Button
                onClick={() => navigate(`/pay-invoice?invoice=${invoice?.invoice_number}`)}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="pay-now-btn"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Pay Now
              </Button>
            )}
          </div>
        </div>

        {/* Invoice Preview */}
        <Card className="bg-white shadow-lg overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-auto" style={{ maxHeight: "80vh" }}>
              <InvoiceTemplate ref={templateRef} invoice={invoice} />
            </div>
          </CardContent>
        </Card>

        {/* Quick Info */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-orange-500" />
                <div>
                  <p className="text-sm text-slate-500">Total Amount</p>
                  <p className="text-lg font-bold text-slate-900">
                    XCD ${invoice?.amount?.toFixed(2)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          {!isQuote && (
            <>
              <Card className="bg-white">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-8 h-8 text-green-500" />
                    <div>
                      <p className="text-sm text-slate-500">Amount Paid</p>
                      <p className="text-lg font-bold text-green-600">
                        XCD ${invoice?.amount_paid?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8 text-red-500" />
                    <div>
                      <p className="text-sm text-slate-500">Balance Due</p>
                      <p className="text-lg font-bold text-red-600">
                        XCD ${(invoice?.amount - invoice?.amount_paid)?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          [data-testid="invoice-template"],
          [data-testid="invoice-template"] * {
            visibility: visible;
          }
          [data-testid="invoice-template"] {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}
